// Firebase owns the session. Native receives short-lived ID tokens only after
// an explicit, main-frame handshake; it never injects a cached account into web.
export function connectMobileAuthBridge({ host, auth, subscribe, signOut, clearLocalPlanning = () => {} }) {
  if (!host || host.top !== host || !host.ReactNativeWebView?.postMessage)
    return () => {};
  let active = true;
  let ready = false;
  let request = null;
  let revision = 0;
  let signingOut = false;

  const clearLegacyTokens = () => {
    try {
      host.localStorage.removeItem('token');
    } catch {}
    try {
      host.sessionStorage.removeItem('authToken');
      host.sessionStorage.removeItem('authTokenExpiry');
    } catch {}
  };
  const send = (current, status, extra = {}) => {
    if (!active || request !== current) return;
    host.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'AUTH_STATE',
        requestId: current.requestId,
        status,
        ...extra,
      })
    );
  };
  const sync = async () => {
    if (!ready || !request || signingOut) return;
    const current = request;
    const operation = ++revision;
    if (current.type === 'SIGN_OUT') {
      signingOut = true;
      clearLegacyTokens();
      try {
        clearLocalPlanning();
        await signOut(auth);
        clearLegacyTokens();
        if (auth.currentUser === null) send(current, 'signed-out');
        else send(current, 'error');
      } catch {
        send(current, 'error');
      } finally {
        signingOut = false;
        if (request !== current) void sync();
      }
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      clearLegacyTokens();
      send(current, 'signed-out');
      return;
    }
    send(current, 'syncing');
    try {
      // getIdToken refreshes expired/nearly expired tokens. A forced refresh
      // inside the token observer could create a refresh loop.
      const token = await user.getIdToken();
      if (!active || operation !== revision || auth.currentUser !== user)
        return;
      send(current, 'signed-in', {
        token,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        },
      });
    } catch {
      if (operation === revision) send(current, 'error');
    }
  };
  const command = (value) => {
    if (
      !value ||
      !['SYNC_AUTH', 'SIGN_OUT'].includes(value.type) ||
      typeof value.requestId !== 'string' ||
      !/^[a-zA-Z0-9_-]{1,100}$/.test(value.requestId)
    )
      return;
    request = { type: value.type, requestId: value.requestId };
    revision += 1;
    void sync();
  };
  const onCommand = (event) => command(event.detail);
  clearLegacyTokens();
  host.addEventListener('odyssey:native-auth-command', onCommand);
  const unsubscribe = subscribe(auth, () => {
    ready = true;
    revision += 1;
    clearLegacyTokens();
    void sync();
  });
  command(host.__ODYSSEY_NATIVE_AUTH_REQUEST__);
  return () => {
    active = false;
    revision += 1;
    unsubscribe();
    host.removeEventListener('odyssey:native-auth-command', onCommand);
  };
}
